import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import {
  createCrmPaymentOperation,
  CrmPaymentServicePayloadItem,
} from '../operations/create-crm-payment-operation';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { CreditCardSelection } from '../../../../../../../pages/user-page/pages/payment-methods/components/credit-card-selection';
import { CreditCardTokenEntity } from '../../../../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { AddNewCardDialog } from '../../../../../../../pages/user-page/pages/payment-methods/components/add-new-card-dialog';
import {
  CrmOperationType,
  CrmPaymentWithDocumentEntity,
} from '../../../../../../../api/types/crm/payments/crm-payments-entity';
import { PaymentMethodsDb } from '../../../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { PaymentServiceItemsEditor } from './payment-service-items-editor';
import { SelectCrmPaymentServiceDialog } from './select-crm-payment-service-dialog';
import {
  PaymentServiceItem,
  SelectedCrmCatalogService,
} from './crm-payment-service-item.type';
import globalSettingsBranches from '../../../../../../../stores/global-settings-branches';
import dateUtils from '../../../../../../../utils/date-format';
import { toast } from 'react-toastify';

interface AddCrmPaymentDialogProps {
  visible: boolean;
  accountId: number | null;
  onHide: () => void;
  onSuccess: () => void;
  creditCardTokens?: CreditCardTokenEntity[];
  /** CRM account display name — default for «לכבוד» (recipient on receipt) */
  defaultReceiptRecipientName?: string;
  /** CRM account registration number — default for receipt identifierId */
  defaultRegistrationNumber?: string;
  /** First contact phone — default for receipt phoneNumber */
  defaultPhoneNumber?: string;
  /**
   * Locks the dialog to a specific operation (no in-dialog operation-type
   * picker). Defaults to 'create-payment' for backward compatibility.
   * - 'create-payment' → operationType=Payment, originalCrmPaymentId omitted.
   * - 'refund'         → operationType=Refund, originalCrmPaymentId taken from
   *   `originalPaymentRow.id`. The dialog pre-fills branch + a single service
   *   line synthesized from the original row.
   */
  mode?: 'create-payment' | 'refund';
  /** Required when `mode === 'refund'`; ignored otherwise. */
  originalPaymentRow?: CrmPaymentWithDocumentEntity | null;
}

export const AddCrmPaymentDialog: React.FC<AddCrmPaymentDialogProps> = ({
  visible,
  accountId,
  onHide,
  onSuccess,
  creditCardTokens = [],
  defaultReceiptRecipientName = '',
  defaultRegistrationNumber = '',
  defaultPhoneNumber = '',
  mode = 'create-payment',
  originalPaymentRow = null,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  // Operation type is decided at the entry point (the table button used to
  // open the dialog), not by the user inside the dialog. This eliminates the
  // "refund without parent payment" failure mode now rejected by the backend.
  const operationType: CrmOperationType =
    mode === 'refund' ? CrmOperationType.Refund : CrmOperationType.Payment;
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodsDb>(
    PaymentMethodsDb.Cash,
  );
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [items, setItems] = useState<PaymentServiceItem[]>([
    { kind: 'adHoc', serviceName: '', units: 1, unitPrice: null },
  ]);
  const [selectedCreditCardTokenId, setSelectedCreditCardTokenId] = useState<
    number | null
  >(null);
  const [numberOfInstallments, setNumberOfInstallments] = useState<number>(1);
  const [bankNumber, setBankNumber] = useState<string>('');
  const [branchNumber, setBranchNumber] = useState<string>('');
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [transferDate, setTransferDate] = useState<Date | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [referenceId, setReferenceId] = useState<string>('');
  const [accountingDocumentSubject, setAccountingDocumentSubject] =
    useState<string>('');
  const [receiptRecipientName, setReceiptRecipientName] = useState('');
  const [receiptIssuedFor, setReceiptIssuedFor] = useState('');
  const [receiptNote, setReceiptNote] = useState('');
  const [receiptRegistrationNumber, setReceiptRegistrationNumber] =
    useState('');
  const [receiptPhoneNumber, setReceiptPhoneNumber] = useState('');
  const [showAddNewCardDialog, setShowAddNewCardDialog] = useState(false);
  const [showSelectCatalogServiceDialog, setShowSelectCatalogServiceDialog] =
    useState(false);

  const branches = globalSettingsBranches.branches ?? [];
  const defaultBranchId = useMemo(() => {
    const idx = globalSettingsBranches.currentBranchIndex;
    if (typeof idx === 'number' && idx >= 0 && idx < branches.length) {
      return branches[idx]?.id ?? null;
    }
    return branches.length === 1 ? branches[0].id : null;
  }, [branches]);

  // When opening the dialog, preselect a sensible default if available.
  useEffect(() => {
    if (!visible) return;
    setSelectedBranchId((prev) => prev ?? defaultBranchId);
  }, [visible, defaultBranchId]);

  // Refund mode: synthesize a single service line from the original payment.
  // Branch comes from the original row when known, otherwise we fall back to
  // the user's default branch (handled by the effect above). Other fields
  // (payment method, recipient, etc.) keep their normal defaults so the user
  // can choose how to deliver the refund.
  useEffect(() => {
    if (!visible) return;
    if (mode !== 'refund' || !originalPaymentRow) return;
    if (originalPaymentRow.branchId != null) {
      setSelectedBranchId(originalPaymentRow.branchId);
    }
    setItems([
      {
        kind: 'adHoc',
        serviceName: `החזר עבור תשלום #${originalPaymentRow.id}`,
        units: 1,
        unitPrice:
          typeof originalPaymentRow.paymentAmount === 'number'
            ? originalPaymentRow.paymentAmount
            : null,
      },
    ]);
  }, [visible, mode, originalPaymentRow]);

  // Default receipt fields when opening (max lengths match backend Joi).
  useEffect(() => {
    if (!visible) return;
    setReceiptRecipientName(defaultReceiptRecipientName.trim().slice(0, 100));
    setReceiptIssuedFor('');
    setReceiptNote('');
    setReceiptRegistrationNumber(defaultRegistrationNumber.trim());
    setReceiptPhoneNumber(defaultPhoneNumber.trim());
  }, [
    visible,
    defaultReceiptRecipientName,
    defaultRegistrationNumber,
    defaultPhoneNumber,
  ]);

  const totalServicesAmount = useMemo(() => {
    return items.reduce((sum, item) => {
      const units = typeof item.units === 'number' ? item.units : 0;
      const unitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : 0;
      return sum + units * unitPrice;
    }, 0);
  }, [items]);

  const isValid = useMemo(() => {
    // Services validation (backend requires at least one valid item)
    if (!items || items.length < 1) return false;
    const allValidItems = items.every((i) => {
      if (typeof i.units !== 'number' || i.units < 1) return false;
      if (i.kind === 'catalog') {
        return (
          i.paymentServiceId > 0 &&
          i.paymentServicePriceId > 0 &&
          typeof i.unitPrice === 'number' &&
          i.unitPrice >= 0
        );
      }
      return (
        i.serviceName.trim().length > 0 &&
        typeof i.unitPrice === 'number' &&
        i.unitPrice >= 0
      );
    });
    if (!allValidItems) return false;
    if (
      !(typeof totalServicesAmount === 'number' && totalServicesAmount >= 0.01)
    )
      return false;
    // Backend requires identifierId + phoneNumber on every payment.
    if (!receiptRegistrationNumber.trim() || !receiptPhoneNumber.trim()) {
      return false;
    }
    switch (paymentMethodType) {
      case PaymentMethodsDb.CreditCard:
        return (
          !!selectedCreditCardTokenId &&
          numberOfInstallments >= 1 &&
          numberOfInstallments <= 10
        );
      case PaymentMethodsDb.BankTransfer:
        return (
          bankNumber.trim().length > 0 &&
          branchNumber.trim().length > 0 &&
          accountNumber.trim().length > 0 &&
          transferDate !== null /*&&
          referenceId.trim().length > 0*/
        );
      case PaymentMethodsDb.Check:
        return (
          bankNumber.trim().length > 0 &&
          branchNumber.trim().length > 0 &&
          accountNumber.trim().length > 0 &&
          dueDate !== null &&
          referenceId.trim().length > 0
        );
      case PaymentMethodsDb.Cash:
      default:
        return true;
    }
  }, [
    paymentMethodType,
    selectedCreditCardTokenId,
    bankNumber,
    branchNumber,
    accountNumber,
    transferDate,
    dueDate,
    items,
    totalServicesAmount,
    referenceId,
    receiptRegistrationNumber,
    receiptPhoneNumber,
  ]);

  const reset = () => {
    setItems([{ kind: 'adHoc', serviceName: '', units: 1, unitPrice: null }]);
    setSelectedBranchId(null);
    setAccountingDocumentSubject('');
    setReceiptRecipientName('');
    setReceiptIssuedFor('');
    setReceiptNote('');
    setReceiptRegistrationNumber('');
    setReceiptPhoneNumber('');
    setShowSelectCatalogServiceDialog(false);
  };

  const handleCatalogServiceSelected = (service: SelectedCrmCatalogService) => {
    setItems((prev) => [
      ...prev,
      {
        kind: 'catalog',
        paymentServiceId: service.paymentServiceId,
        paymentServicePriceId: service.paymentServicePriceId,
        serviceName: service.serviceName,
        unitPrice: service.unitPrice,
        units: 1,
      },
    ]);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onHide();
  };

  const handleApprove = async () => {
    if (!accountId || !isValid || loading) return;

    const branchIdToSend = selectedBranchId ?? defaultBranchId;
    /*if (!branchIdToSend) {
      toast.warn('יש לבחור שלוחה');
      return;
    }*/

    await createCrmPaymentOperation(
      accountId,
      {
        branchId: branchIdToSend,
        paymentAmount: Number(totalServicesAmount.toFixed(2)),
        operationType: operationType as CrmOperationType,
        paymentMethodType,
        services: items.map((i): CrmPaymentServicePayloadItem => {
          if (i.kind === 'catalog') {
            return {
              paymentServiceId: i.paymentServiceId,
              paymentServicePriceId: i.paymentServicePriceId,
              units: i.units as number,
            };
          }
          return {
            serviceName: i.serviceName.trim(),
            units: i.units as number,
            unitPrice: Number((i.unitPrice as number).toFixed(2)),
          };
        }),
        accountingDocumentSubject: accountingDocumentSubject.trim() || null,
        recipientName: receiptRecipientName,
        identifierId: receiptRegistrationNumber,
        phoneNumber: receiptPhoneNumber,
        issuedFor: receiptIssuedFor,
        note: receiptNote,
        paymentMethodDetails:
          paymentMethodType === PaymentMethodsDb.CreditCard &&
          selectedCreditCardTokenId
            ? {
                creditCardTokenId: selectedCreditCardTokenId,
                numberOfInstallments,
              }
            : paymentMethodType === PaymentMethodsDb.BankTransfer &&
                transferDate !== null
              ? {
                  bankNumber: bankNumber.trim(),
                  branchNumber: branchNumber.trim(),
                  accountNumber: accountNumber.trim(),
                  transferDate: dateUtils.formattedDateWithNoTime(transferDate),
                  referenceId: referenceId.trim(),
                }
              : paymentMethodType === PaymentMethodsDb.Check &&
                  dueDate !== null &&
                  referenceId.trim().length > 0
                ? {
                    bankNumber: bankNumber.trim(),
                    branchNumber: branchNumber.trim(),
                    accountNumber: accountNumber.trim(),
                    dueDate: dateUtils.formattedDateWithNoTime(dueDate),
                    referenceId: referenceId.trim(),
                  }
                : undefined,
        // Joi `forbidden` for non-refund flows requires us to omit the key
        // entirely; the operation layer also guards this with a conditional
        // spread so a stray `undefined` never reaches the wire.
        ...(mode === 'refund' && originalPaymentRow
          ? { originalCrmPaymentId: originalPaymentRow.id }
          : {}),
      },
      setLoading,
      () => {
        handleClose();
        onSuccess();
      },
    );
  };

  const dialogHeader =
    mode === 'refund' && originalPaymentRow
      ? `הוספת החזר עבור תשלום #${originalPaymentRow.id}`
      : 'הוספת תשלום';

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={handleClose}
      style={{ width: '700px', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label={loading ? 'שומר...' : 'אישור'}
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleApprove}
            disabled={!accountId || !isValid || loading}
            loading={loading}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <PaymentServiceItemsEditor
          items={items}
          setItems={setItems}
          loading={loading}
          totalServicesAmount={totalServicesAmount}
          onAddCatalogService={() => setShowSelectCatalogServiceDialog(true)}
        />

        <style>
          {`
            .crm-add-payment-receipt-panel {
              border-radius: 12px;
              border: 1px solid #e2e8f0;
              background: linear-gradient(165deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%);
              box-shadow:
                0 4px 6px -1px rgba(15, 23, 42, 0.07),
                0 2px 4px -2px rgba(15, 23, 42, 0.05);
              border-inline-start: 4px solid #0d9488;
              padding: 1.125rem 1.25rem;
              min-width: 0;
              width: 100%;
              box-sizing: border-box;
            }
            .crm-add-payment-receipt-panel .receipt-panel-header {
              display: flex;
              align-items: flex-start;
              gap: 0.75rem;
              margin-bottom: 1rem;
            }
            .crm-add-payment-receipt-panel .receipt-panel-icon-wrap {
              flex-shrink: 0;
              width: 2.5rem;
              height: 2.5rem;
              border-radius: 10px;
              background: linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%);
              color: #0f766e;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 1.125rem;
              box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
            }
            .crm-add-payment-receipt-panel .receipt-panel-title {
              margin: 0;
              font-size: 1.125rem;
              font-weight: 700;
              color: #0f172a;
              letter-spacing: -0.02em;
              line-height: 1.3;
            }
            .crm-add-payment-receipt-panel .receipt-panel-kicker {
              display: block;
              font-size: 0.75rem;
              font-weight: 500;
              color: #64748b;
              margin-top: 0.2rem;
            }
            .crm-add-payment-receipt-panel .receipt-panel-info {
              display: flex;
              align-items: flex-start;
              gap: 0.5rem;
              font-size: 0.8125rem;
              line-height: 1.65;
              color: #475569;
              background: #f0fdfa;
              border: 1px solid #99f6e4;
              border-radius: 8px;
              padding: 0.75rem 0.875rem;
              margin-bottom: 1.125rem;
            }
            .crm-add-payment-receipt-panel .receipt-panel-info .pi-info-circle {
              flex-shrink: 0;
              margin-top: 0.12rem;
              color: #0d9488;
              font-size: 0.95rem;
            }
            .crm-add-payment-receipt-panel .crm-receipt-fields-grid {
              display: grid;
              grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
              column-gap: 0.5rem;
              row-gap: 0.75rem;
              align-items: start;
              width: 100%;
            }
            .crm-add-payment-receipt-panel .receipt-field-group {
              width: 100%;
              min-width: 0;
              box-sizing: border-box;
            }
            .crm-add-payment-receipt-panel .crm-receipt-fields-grid .receipt-field-group {
              margin-bottom: 0;
            }
            .crm-add-payment-receipt-panel .crm-receipt-note-span {
              grid-column: 1 / -1;
              width: 100%;
            }
            @media (max-width: 520px) {
              .crm-add-payment-receipt-panel .crm-receipt-fields-grid {
                grid-template-columns: minmax(0, 1fr);
              }
            }
            .crm-add-payment-receipt-panel .receipt-field-label {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
              gap: 0.35rem;
              font-size: 0.875rem;
              font-weight: 600;
              color: #334155;
              margin-bottom: 0.4rem;
            }
            .crm-add-payment-receipt-panel .receipt-optional-pill {
              font-size: 0.65rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: #64748b;
              background: #e2e8f0;
              padding: 0.15rem 0.45rem;
              border-radius: 4px;
            }
            .crm-add-payment-receipt-panel .receipt-field-hint {
              font-size: 0.6875rem;
              color: #94a3b8;
              margin-top: 0.3rem;
            }
            .crm-add-payment-receipt-panel .p-inputtext {
              width: 100%;
              max-width: 100%;
              box-sizing: border-box;
              border-radius: 8px;
              border-color: #cbd5e1;
              transition: border-color 0.15s ease, box-shadow 0.15s ease;
            }
            .crm-add-payment-receipt-panel .p-inputtext:enabled:hover {
              border-color: #94a3b8;
            }
            .crm-add-payment-receipt-panel .p-inputtext:enabled:focus {
              border-color: #0d9488;
              box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.18);
            }
            .crm-add-payment-receipt-panel .p-inputtextarea {
              width: 100%;
              max-width: 100%;
              box-sizing: border-box;
              border-radius: 8px;
              border-color: #cbd5e1;
              transition: border-color 0.15s ease, box-shadow 0.15s ease;
              min-height: 3.25rem;
              resize: vertical;
            }
            .crm-add-payment-receipt-panel .p-inputtextarea:enabled:hover {
              border-color: #94a3b8;
            }
            .crm-add-payment-receipt-panel .p-inputtextarea:enabled:focus {
              border-color: #0d9488;
              box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.18);
            }
          `}
        </style>
        <div className="crm-add-payment-receipt-panel">
          <div className="receipt-panel-header">
            <div className="receipt-panel-icon-wrap" aria-hidden>
              <i className="pi pi-file-edit" />
            </div>
            <div>
              <h3 className="receipt-panel-title">פרטי קבלה</h3>
              <span className="receipt-panel-kicker">
                יוצגו במסמך הקבלה לאחר יצירת התשלום
              </span>
            </div>
          </div>
          <div className="receipt-panel-info">
            <i className="pi pi-info-circle" aria-hidden />
            <span>
              הפרטים יודפסו בקבלה. «לכבוד» ו-«עבור» — עד 100 תווים כל אחד; ברירת
              מחדל ל«לכבוד»: שם החשבון ב-CRM. «מס׳ רישום» ו-«טלפון» נדרשים.
              «הערה» — אופציונלי, עד 200 תווים. שדה «נושא מסמך חשבונאי» למעלה
              הוא נפרד ומשמש למסמכים חשבונאיים.
            </span>
          </div>
          <div className="crm-receipt-fields-grid" style={{ width: '100%' }}>
            <div className="receipt-field-group">
              <label
                htmlFor="crm-receipt-recipient-name"
                className="receipt-field-label"
              >
                לכבוד
              </label>
              <InputText
                id="crm-receipt-recipient-name"
                value={receiptRecipientName}
                onChange={(e) => setReceiptRecipientName(e.target.value)}
                className="w-full"
                maxLength={100}
                dir="rtl"
                disabled={loading}
              />
              <div className="receipt-field-hint">עד 100 תווים</div>
            </div>
            <div className="receipt-field-group">
              <label
                htmlFor="crm-receipt-registration-number"
                className="receipt-field-label"
              >
                מס׳ רישום
              </label>
              <InputText
                id="crm-receipt-registration-number"
                value={receiptRegistrationNumber}
                onChange={(e) => setReceiptRegistrationNumber(e.target.value)}
                className="w-full"
                dir="rtl"
                disabled={loading}
              />
            </div>
            <div className="receipt-field-group">
              <label
                htmlFor="crm-receipt-phone-number"
                className="receipt-field-label"
              >
                טלפון
              </label>
              <InputText
                id="crm-receipt-phone-number"
                value={receiptPhoneNumber}
                onChange={(e) => setReceiptPhoneNumber(e.target.value)}
                className="w-full"
                dir="rtl"
                disabled={loading}
              />
            </div>
            <div className="receipt-field-group">
              <label
                htmlFor="crm-receipt-issued-for"
                className="receipt-field-label"
              >
                עבור
                <span className="receipt-optional-pill">אופציונלי</span>
              </label>
              <InputText
                id="crm-receipt-issued-for"
                value={receiptIssuedFor}
                onChange={(e) => setReceiptIssuedFor(e.target.value)}
                className="w-full"
                maxLength={100}
                dir="rtl"
                disabled={loading}
                placeholder="למשל: מבחן תיל"
              />
              <div className="receipt-field-hint">עד 100 תווים</div>
            </div>
            <div className="receipt-field-group crm-receipt-note-span">
              <label htmlFor="crm-receipt-note" className="receipt-field-label">
                הערה
                <span className="receipt-optional-pill">אופציונלי</span>
              </label>
              <InputTextarea
                id="crm-receipt-note"
                value={receiptNote}
                onChange={(e) => setReceiptNote(e.target.value)}
                className="w-full"
                maxLength={200}
                rows={2}
                autoResize
                dir="rtl"
                disabled={loading}
                placeholder="הערה לתיעוד (אופציונלי)…"
              />
              <div className="receipt-field-hint">עד 200 תווים</div>
            </div>
          </div>
        </div>

        <div className="p-field">
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}
          >
            שלוחה
          </label>
          <Dropdown
            value={selectedBranchId}
            options={branches.map((b) => ({
              label: b.branchName,
              value: b.id,
            }))}
            onChange={(e) =>
              setSelectedBranchId((e.value ?? null) as number | null)
            }
            optionLabel="label"
            optionValue="value"
            style={{ width: '100%' }}
            disabled={loading}
            placeholder="בחר שלוחה (אופציונלי)"
          />
        </div>

        <div className="p-field">
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}
          >
            אמצעי תשלום *
          </label>
          <Dropdown
            value={paymentMethodType}
            options={[
              { label: 'מזומן', value: PaymentMethodsDb.Cash },
              { label: 'כרטיס אשראי', value: PaymentMethodsDb.CreditCard },
              { label: 'העברה בנקאית', value: PaymentMethodsDb.BankTransfer },
              { label: 'המחאה', value: PaymentMethodsDb.Check },
            ]}
            onChange={(e) => setPaymentMethodType(e.value)}
            optionLabel="label"
            optionValue="value"
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>
        {paymentMethodType === PaymentMethodsDb.CreditCard && (
          <CreditCardSelection
            creditCardTokens={creditCardTokens}
            selectedCreditCardTokenId={selectedCreditCardTokenId}
            onCreditCardChange={(id: any) =>
              setSelectedCreditCardTokenId((id ?? null) as number | null)
            }
            onAddNewCardClick={() => {
              setShowAddNewCardDialog(true);
            }}
          />
        )}
        {paymentMethodType === PaymentMethodsDb.CreditCard && (
          <div className="p-field">
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
              }}
            >
              מספר תשלומים *
            </label>
            <InputNumber
              value={numberOfInstallments}
              onValueChange={(e) =>
                setNumberOfInstallments((e.value as number) ?? 1)
              }
              min={1}
              max={10}
              showButtons
              inputStyle={{ textAlign: 'right' }}
              disabled={loading}
              style={{ width: '100%', direction: 'ltr' }}
            />
          </div>
        )}
        {paymentMethodType === PaymentMethodsDb.BankTransfer && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            <div className="p-field">
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מס׳ בנק *
              </label>
              <InputText
                value={bankNumber}
                onChange={(e) =>
                  setBankNumber(e.target.value.replace(/\D/g, ''))
                }
                keyfilter="int"
                inputMode="numeric"
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
            <div className="p-field">
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מס׳ סניף *
              </label>
              <InputText
                value={branchNumber}
                onChange={(e) =>
                  setBranchNumber(e.target.value.replace(/\D/g, ''))
                }
                keyfilter="int"
                inputMode="numeric"
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
            <div className="p-field" style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מס׳ חשבון *
              </label>
              <InputText
                value={accountNumber}
                onChange={(e) =>
                  setAccountNumber(e.target.value.replace(/\D/g, ''))
                }
                keyfilter="int"
                inputMode="numeric"
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
            <div className="p-field" style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                תאריך העברה *
              </label>
              <Calendar
                value={transferDate}
                onChange={(e) => setTransferDate(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                style={{ width: '100%', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                disabled={loading}
              />
            </div>
            <div className="p-field" style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מזהה אסמכתא *
              </label>
              <InputText
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
          </div>
        )}
        {paymentMethodType === PaymentMethodsDb.Check && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            <div className="p-field">
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מס׳ בנק *
              </label>
              <InputText
                value={bankNumber}
                onChange={(e) =>
                  setBankNumber(e.target.value.replace(/\D/g, ''))
                }
                keyfilter="int"
                inputMode="numeric"
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
            <div className="p-field">
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מס׳ סניף *
              </label>
              <InputText
                value={branchNumber}
                onChange={(e) =>
                  setBranchNumber(e.target.value.replace(/\D/g, ''))
                }
                keyfilter="int"
                inputMode="numeric"
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
            <div className="p-field" style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מס׳ חשבון *
              </label>
              <InputText
                value={accountNumber}
                onChange={(e) =>
                  setAccountNumber(e.target.value.replace(/\D/g, ''))
                }
                keyfilter="int"
                inputMode="numeric"
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
            <div className="p-field">
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                תאריך פירעון *
              </label>
              <Calendar
                value={dueDate}
                onChange={(e) => setDueDate(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                style={{ width: '100%', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                disabled={loading}
              />
            </div>
            <div className="p-field">
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                מזהה המחאה *
              </label>
              <InputText
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
          </div>
        )}
      </div>
      <AddNewCardDialog
        visible={showAddNewCardDialog}
        onHide={() => setShowAddNewCardDialog(false)}
        userId={null}
        crmAccountId={accountId ?? null}
        onSuccess={() => {
          // Close the add-card dialog and refresh account details (tokens, etc.)
          setShowAddNewCardDialog(false);
          onSuccess();
        }}
      />
      <SelectCrmPaymentServiceDialog
        visible={showSelectCatalogServiceDialog}
        onHide={() => setShowSelectCatalogServiceDialog(false)}
        onSelect={handleCatalogServiceSelected}
      />
    </Dialog>
  );
};
